import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useRef, useState, type InputHTMLAttributes } from "react";

const US_STATES = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
  "District of Columbia",
] as const;
const HOURS_OPTIONS = ["5 Days", "6 Days", "7 Days", "24/7"] as const;
const ACCESS_OPTIONS = ["Easy", "Hard", "None"] as const;
const MACHINE_OPTIONS = ["Drink", "Snack", "Combo", "Coffee", "Food", "Ice"] as const;
import logoAsset from "../assets/logo.webp.asset.json";
import logo400 from "../assets/logo-400.webp.asset.json";
import logo800 from "../assets/logo-800.webp.asset.json";
import logo1200 from "../assets/logo-1200.webp.asset.json";

const LOGO_SRCSET = `${logo400.url} 400w, ${logo800.url} 800w, ${logo1200.url} 1200w, ${logoAsset.url} 1920w`;
import urbanFuelImg from "../assets/urban-fuel-smart-vending-office-lobby.png.asset.json";

import corporateLobbyImg from "../assets/corporate-lobby-hero-3-vending.png.asset.json";
import smartFridgeImg from "../assets/smart-fridge-freezer-24h-corporate.png.asset.json";

// Lazy USAMap — heavy SVG component, defers ~40kb JS + paint work off critical path
const USAMap = lazy(() =>
  import("@/components/USAMap").then((m) => ({ default: m.USAMap })),
);
import {
  ArrowRight,
  CheckCircle2,
  ClipboardCheck,
  Truck,
  Wrench,
  Repeat,
  Building2,
  Hospital,
  GraduationCap,
  Factory,
  Hotel,
  Dumbbell,
  ShieldCheck,
  MapPin,
  Clock,
  DollarSign,
  Sparkles,
  Pickaxe,
  Shield,
  Plane,
} from "lucide-react";
import { ogImageMeta } from "@/lib/og-image";

const META_TITLE = "Free Vending Machine — Nationwide Placement USA";
const META_DESC =
  "Get a free vending machine for your business — anywhere in the USA. Free install, restocking & maintenance. No cost, no contracts. Apply now for fast approval.";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: META_TITLE },
      { name: "description", content: META_DESC },
      { property: "og:title", content: META_TITLE },
      { property: "og:description", content: META_DESC },
      { property: "og:url", content: "https://vendingmachinefree.com/" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Vending Machine Free.com" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: META_TITLE },
      { name: "twitter:description", content: META_DESC },
      ...ogImageMeta(),
    ],
    links: [
      { rel: "canonical", href: "https://vendingmachinefree.com/" },
      {
        rel: "preload",
        as: "image",
        href: logo800.url,
        imageSrcSet: LOGO_SRCSET,
        imageSizes: "(max-width: 640px) 240px, (max-width: 1024px) 340px, 420px",
        fetchpriority: "high",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Vending Machine Free.com",
          url: "https://vendingmachinefree.com/",
          areaServed: "US",
          slogan: "Powered by American businesses — get a Free Vending Machine Nationwide.",
          description: META_DESC,
          contactPoint: [
            {
              "@type": "ContactPoint",
              contactType: "customer service",
              areaServed: "US",
              availableLanguage: ["English"],
              url: "https://vendingmachinefree.com/#contact",
              // Inquiries are submitted via the website contact form — no phone/email exposed
              contactOption: "OnlineChat",
            },
          ],
          sameAs: [
            "https://www.facebook.com/vendingmachinesfree",
            "https://www.linkedin.com/company/vendingmachines-free",
            "https://www.instagram.com/vendingmachinesfree",
            "https://twitter.com/vending_free",
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": "https://vendingmachinefree.com/#business",
          name: "Vending Machine Free.com",
          image: "https://vendingmachinefree.com" + logoAsset.url,
          logo: "https://vendingmachinefree.com" + logoAsset.url,
          url: "https://vendingmachinefree.com/",
          priceRange: "Free",
          description: META_DESC,
          areaServed: { "@type": "Country", name: "United States" },
          serviceArea: { "@type": "Country", name: "United States" },
          makesOffer: {
            "@type": "Offer",
            name: "Free Vending Machine Placement",
            price: "0",
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
            eligibleRegion: { "@type": "Country", name: "United States" },
            description:
              "Free supply, installation, restocking, and service of modern vending machines for qualified U.S. businesses.",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Contact Vending Machine Free.com",
          url: "https://vendingmachinefree.com/#contact",
          description:
            "Request your FREE vending machine placement. Inquiries are submitted via our online application form — our team responds within 24 hours.",
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": "https://vendingmachinefree.com/#contact",
          },
          potentialAction: {
            "@type": "CommunicateAction",
            name: "Submit Vending Machine Application",
            target: "https://vendingmachinefree.com/#contact",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://vendingmachinefree.com/",
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map(({ q, a }) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Industries We Serve",
          itemListElement: [
            { slug: "offices-corporate", name: "Offices & Corporate" },
            { slug: "warehouses-manufacturing", name: "Warehouses & Manufacturing" },
            { slug: "hospitals-clinics", name: "Hospitals & Clinics" },
            { slug: "schools-universities", name: "Schools & Universities" },
            { slug: "hotels-hospitality", name: "Hotels & Hospitality" },
            { slug: "gyms-fitness-centers", name: "Gyms & Fitness Centers" },
            { slug: "mines-mining-sites", name: "Mines & Mining Sites" },
            { slug: "military-bases", name: "Military Bases & Defense Facilities" },
            { slug: "airports-aviation", name: "Airports & Aviation Facilities" },
          ].map((i, idx) => ({
            "@type": "ListItem",
            position: idx + 1,
            name: i.name,
            url: `https://vendingmachinefree.com/industries/${i.slug}`,
          })),
        }),
      },
    ],
  }),
  component: Home,
});

const STATES = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
];

const STEPS = [
  {
    icon: ClipboardCheck,
    title: "1. Apply Online (Free)",
    text: "Submit our 2-minute form with your business type, headcount, and ZIP code. Zero commitment — we confirm eligibility within 4–6 business hours.",
  },
  {
    icon: CheckCircle2,
    title: "2. Free Site Survey & Machine Match",
    text: "A local operator visits your site, measures the space and power, and matches you with the right snack, drink, coffee, or combo machines — all at no cost.",
  },
  {
    icon: Truck,
    title: "3. Free Supply, Delivery & Install",
    text: "We supply the machines, deliver them to your business, install them, plug them in, load the initial inventory, and test cashless payment — all included.",
  },
  {
    icon: Repeat,
    title: "4. Ongoing Restocking & Service",
    text: "Route drivers restock on a weekly schedule; technicians handle cleaning, cash collection, repairs, and 24/7 support. You pay $0 — ever.",
  },
];

const SERVES: { icon: typeof Building2; label: string; slug: string }[] = [
  { icon: Building2, label: "Offices & Corporate", slug: "offices-corporate" },
  { icon: Factory, label: "Warehouses & Manufacturing", slug: "warehouses-manufacturing" },
  { icon: Hospital, label: "Hospitals & Clinics", slug: "hospitals-clinics" },
  { icon: GraduationCap, label: "Schools & Universities", slug: "schools-universities" },
  { icon: Hotel, label: "Hotels & Hospitality", slug: "hotels-hospitality" },
  { icon: Dumbbell, label: "Gyms & Fitness Centers", slug: "gyms-fitness-centers" },
  { icon: Pickaxe, label: "Mines & Mining Sites", slug: "mines-mining-sites" },
  { icon: Shield, label: "Military Bases", slug: "military-bases" },
  { icon: Plane, label: "Airports & Aviation", slug: "airports-aviation" },
];

const REASONS = [
  {
    icon: DollarSign,
    title: "100% Free",
    text: "No purchase, lease, or hidden fees. The machines, install, and service are on us.",
  },
  {
    icon: Wrench,
    title: "Full-Service",
    text: "Restocking, cleaning, repairs, and cashless payment upgrades — handled end-to-end.",
  },
  {
    icon: MapPin,
    title: "Nationwide Coverage",
    text: "Operating in all 50 states with regional route operators near your facility.",
  },
  {
    icon: ShieldCheck,
    title: "Trusted Brands",
    text: "Pepsi, Coca-Cola, and healthy snack lines tailored to your team's preferences.",
  },
  {
    icon: Clock,
    title: "Fast Approval",
    text: "Most qualified locations are approved within 48 hours of applying.",
  },
  {
    icon: Sparkles,
    title: "Modern Machines",
    text: "Latest models with touchscreens, tap-to-pay, and energy-efficient cooling.",
  },
];

const FAQS = [
  {
    q: "How to get a vending machine for my business?",
    a: "The easiest way to get a vending machine is to apply for free placement with a full-service vending operator. Submit our 2-minute form with your business type, ZIP code, and daily headcount. If you qualify, a local operator will supply, deliver, install, stock, and service the machine at 100% no cost — you don't buy or lease anything.",
  },
  {
    q: "How to get free vending for my business?",
    a: "To get free vending for your business, you need a location with ~25+ employees or 100+ daily visitors, a standard power outlet, and space for the machine. Apply online, complete a free site survey, and once approved we install and restock the machines at zero cost. We earn from product sales, so qualifying locations pay nothing.",
  },
  {
    q: "Are the vending machines really free?",
    a: "Yes. We supply, install, stock, and service the machines at no cost to your business. We earn revenue from product sales, so qualified locations pay nothing — no purchase, no lease, no service fee.",
  },
  {
    q: "What does a business need to qualify?",
    a: "Typically 25+ daily foot traffic or employees on-site, a standard power outlet, and space for the machine. We confirm eligibility during the free site survey.",
  },
  {
    q: "Which states do you serve?",
    a: "All 50 U.S. states. We operate a nationwide network of route operators, so coverage is available from Alaska to Florida.",
  },
  {
    q: "How long does installation take?",
    a: "Most qualified locations are surveyed within 48 hours and have machines installed within 1–2 weeks of approval.",
  },
  {
    q: "What products are stocked?",
    a: "We offer Coca-Cola, Pepsi, water, snacks, and healthy options. Product mix is tailored to your team's preferences and updated based on sales data.",
  },
  {
    q: "Who handles repairs and restocking?",
    a: "We do — 100%. Our technicians and route drivers handle all restocking, cleaning, cash collection, and repairs at no charge.",
  },
];

type FieldErrors = Partial<Record<string, string>>;

import { getTurnstileSiteKey } from "@/lib/turnstile";
const TURNSTILE_ENV_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

type UtmParams = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
};

function readUtmParams(): UtmParams {
  if (typeof window === "undefined") return {};
  try {
    const params = new URLSearchParams(window.location.search);
    const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
    const fromUrl: UtmParams = {};
    let hasAny = false;
    for (const k of keys) {
      const v = params.get(k);
      if (v) {
        fromUrl[k] = v.slice(0, 120);
        hasAny = true;
      }
    }
    if (hasAny) {
      try {
        sessionStorage.setItem("vm_utm", JSON.stringify(fromUrl));
      } catch {
        /* ignore */
      }
      return fromUrl;
    }
    const stored = sessionStorage.getItem("vm_utm");
    return stored ? (JSON.parse(stored) as UtmParams) : {};
  } catch {
    return {};
  }
}

function fireGa4(event: string, params: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  try {
    const w = window as unknown as {
      gtag?: (...args: unknown[]) => void;
      dataLayer?: unknown[];
    };
    if (typeof w.gtag === "function") {
      w.gtag("event", event, params);
    } else {
      (w.dataLayer = w.dataLayer || []).push({ event, ...params });
    }
  } catch (e) {
    console.warn("[ga4] event failed", e);
  }
}

function Home() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formStartedAt] = useState(() => Date.now());
  const [utm, setUtm] = useState<UtmParams>({});
  useEffect(() => {
    setUtm(readUtmParams());
  }, []);
  const [captchaToken, setCaptchaToken] = useState<string>("");
  const [captchaStatus, setCaptchaStatus] = useState<
    "idle" | "loading" | "ready" | "expired" | "error"
  >(TURNSTILE_ENV_KEY ? "loading" : "idle");

  // Turnstile is now lazy-loaded when the contact section approaches the viewport.
  // This shaves the ~200kb Turnstile bundle + third-party script off initial load
  // and reduces TBT on the homepage.
  const contactRef = useRef<HTMLElement | null>(null);
  const [captchaVisible, setCaptchaVisible] = useState(false);
  // Resolved on client only — swaps to Cloudflare test key on preview hosts
  // to avoid Error 110200 (domain not in allow-list) while keeping the
  // production sitekey on the live custom domains.
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string | undefined>(TURNSTILE_ENV_KEY);
  useEffect(() => {
    setTurnstileSiteKey(getTurnstileSiteKey());
  }, []);
  useEffect(() => {
    if (typeof window === "undefined" || !TURNSTILE_ENV_KEY) return;
    const node = contactRef.current;
    if (!node) return;
    if (!("IntersectionObserver" in window)) {
      setCaptchaVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setCaptchaVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: "600px 0px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !TURNSTILE_ENV_KEY || !captchaVisible) return;
    const w = window as unknown as {
      __vmTurnstileCb?: (t: string) => void;
      __vmTurnstileExpired?: () => void;
      __vmTurnstileError?: () => void;
    };
    w.__vmTurnstileCb = (t: string) => {
      setCaptchaToken(t);
      setCaptchaStatus("ready");
      setFieldErrors((p) => ({ ...p, turnstile_token: undefined }));
    };
    w.__vmTurnstileExpired = () => {
      setCaptchaToken("");
      setCaptchaStatus("expired");
    };
    w.__vmTurnstileError = () => {
      setCaptchaToken("");
      setCaptchaStatus("error");
    };
    if (!document.querySelector('script[data-turnstile="1"]')) {
      const s = document.createElement("script");
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      s.async = true;
      s.defer = true;
      s.dataset.turnstile = "1";
      s.onerror = () => setCaptchaStatus("error");
      document.head.appendChild(s);
    }
  }, [captchaVisible]);

  const resetCaptcha = () => {
    setCaptchaToken("");
    setCaptchaStatus("loading");
    const w = window as unknown as {
      turnstile?: { reset: (sel?: string) => void };
    };
    try {
      w.turnstile?.reset(".cf-turnstile");
    } catch {
      /* widget may not be ready */
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <header className="border-b border-border bg-background/90 backdrop-blur sticky top-0 z-40">
        <div className="container-x flex h-20 md:h-28 items-center justify-between gap-3">
          <a href="/" className="flex min-w-0 items-center gap-2">
            <img
              src={logo800.url}
              srcSet={LOGO_SRCSET}
              sizes="(max-width: 640px) 240px, (max-width: 1024px) 340px, 420px"
              alt="Vending Machine Free — Free vending machines nationwide USA"
              width={1920}
              height={960}
              fetchPriority="high"
              decoding="async"
              className="h-14 sm:h-16 md:h-20 lg:h-24 w-auto"
            />
          </a>

          <nav className="hidden lg:flex items-center gap-6 text-sm font-medium whitespace-nowrap">
            <a href="#how" className="hover:text-safety">
              How It Works
            </a>
            <a href="#serve" className="hover:text-safety">
              Who We Serve
            </a>
            <a href="#coverage" className="hover:text-safety">
              Coverage
            </a>
            <a href="/vending-machines" className="hover:text-safety">
              Machines
            </a>
            <a href="/about" className="hover:text-safety">
              About
            </a>
            <a href="#faq" className="hover:text-safety">
              FAQ
            </a>
            <a href="/contact" className="hover:text-safety">
              Contact
            </a>
          </nav>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 rounded-md bg-safety px-4 py-2 text-sm font-semibold text-safety-foreground hover:opacity-95"
          >
            Apply Free <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </header>

      {/* Hero */}
      <section
        className="relative overflow-hidden text-steel-foreground"
        style={{ background: "var(--gradient-hero)" }}
      >
        <div className="absolute inset-0 stripes-bg opacity-30" aria-hidden />
        <div className="container-x relative py-12 sm:py-16 md:py-24">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
              <span className="h-1.5 w-1.5 rounded-full bg-safety" /> USA Nationwide · 100% Free
            </div>
            <h1 className="mt-4 font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] uppercase break-words">
              Free Vending Machine Placement — Nationwide USA
            </h1>
            <p className="mt-5 text-lg md:text-xl text-white/85 max-w-2xl">
              Get a free vending machine for your business — we supply, install, restock, and
              service modern snack, drink, coffee & combo machines across the United States at 100% no cost.
            </p>
            {/* Zip Qualifier — instant lead capture */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const fd = new FormData(e.currentTarget);
                const zip = String(fd.get("hero_zip") || "").trim();
                fireGa4("zip_qualifier_submit", {
                  cta_location: "hero",
                  zip_valid: /^\d{5}$/.test(zip),
                  ...utm,
                });
                const zipField = document.getElementById("field-zip") as HTMLInputElement | null;
                if (zipField && zip) {
                  zipField.value = zip;
                  zipField.dispatchEvent(new Event("input", { bubbles: true }));
                }
                document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="mt-8 mx-auto max-w-xl"
              aria-label="Check if your business qualifies for a free vending machine"
            >
              <label
                htmlFor="hero-zip"
                className="block text-sm font-semibold uppercase tracking-wider text-white/90 mb-2"
              >
                Enter Zip Code to See If Your Business Qualifies for Free Placement
              </label>
              <div className="flex flex-col sm:flex-row gap-2 rounded-lg bg-white/10 p-2 border border-white/20 backdrop-blur">
                <input
                  id="hero-zip"
                  name="hero_zip"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{5}"
                  maxLength={5}
                  required
                  placeholder="e.g. 90210"
                  className="flex-1 rounded-md bg-white text-charcoal placeholder:text-charcoal/50 px-4 py-3 text-base font-semibold focus:outline-none focus:ring-2 focus:ring-safety"
                />
                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-safety px-6 py-3 font-semibold text-safety-foreground shadow-[var(--shadow-cta)] hover:translate-y-[-1px] transition"
                >
                  Check Eligibility <ArrowRight className="h-4 w-4" />
                </button>
              </div>
              <p className="mt-2 text-xs text-white/70">
                Free installation · Free restocking · 4–6 hour response · 100% no-cost
              </p>
            </form>

            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <a
                href="#contact"
                onClick={() =>
                  fireGa4("cta_click", {
                    cta_id: "hero_primary",
                    cta_label: "Get FREE Vending Machines — USA Nationwide Services",
                    cta_location: "hero",
                    cta_variant: "primary",
                    ...utm,
                  })
                }
                className="inline-flex items-center gap-2 rounded-md bg-safety px-6 py-3.5 font-semibold text-safety-foreground shadow-[var(--shadow-cta)] hover:translate-y-[-1px] transition"
              >
                Get FREE Vending Machines — USA Nationwide Services
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="#contact"
                onClick={() =>
                  fireGa4("cta_click", {
                    cta_id: "hero_secondary",
                    cta_label: "Get Your Free Vending Machines Now",
                    cta_location: "hero",
                    cta_variant: "secondary",
                    ...utm,
                  })
                }
                className="inline-flex items-center gap-2 rounded-md border border-white/30 bg-white/5 px-6 py-3.5 font-semibold text-white hover:bg-white/10"
              >
                Get Your Free Vending Machines Now
              </a>
            </div>


            <div className="mt-10">
              <p className="text-xs uppercase tracking-[0.2em] text-white/60 mb-4">
                Trusted Brands & Coverage
              </p>
              <div className="flex flex-wrap justify-center gap-2.5">
                {["Pepsi", "Coca-Cola", "Healthy Vending", "USA Nationwide Coverage"].map((b) => (
                  <span
                    key={b}
                    className="inline-flex items-center gap-2 rounded-md border border-white/20 bg-white/10 px-3.5 py-2 text-sm font-medium"
                  >
                    <ShieldCheck className="h-4 w-4 text-safety" /> {b}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how" className="py-20 md:py-24">
        <div className="container-x">
          <SectionHeader
            eyebrow="Process"
            title="How It Works"
            subtitle="Four steps from inquiry to fully serviced vending — at zero cost."
          />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="group relative rounded-lg border border-border bg-card p-6 hover:border-safety transition"
              >
                <div className="h-12 w-12 rounded-md bg-steel text-steel-foreground flex items-center justify-center">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 font-display text-xl uppercase">{title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section id="serve" className="py-20 md:py-24 bg-secondary">
        <div className="container-x">
          <SectionHeader
            eyebrow="Industries"
            title="Who We Serve"
            subtitle="If your team, members, or visitors need quick access to drinks and snacks, we serve you."
          />
          <div className="mt-12 grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {SERVES.map(({ icon: Icon, label, slug }) => (
              <Link
                key={label}
                to="/industries/$industry"
                params={{ industry: slug }}
                className="flex items-center gap-4 rounded-lg bg-card p-5 border border-border hover:border-safety hover:shadow-md transition group"
              >
                <div className="h-12 w-12 rounded-md bg-safety/10 text-safety flex items-center justify-center">
                  <Icon className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  <span className="font-semibold block group-hover:text-safety transition">
                    {label}
                  </span>
                  <span className="text-xs text-muted-foreground">View industry page →</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Product Showcase */}
      <section className="py-20 md:py-24">
        <div className="container-x">
          <SectionHeader
            eyebrow="Equipment"
            title="Modern Vending Machines, Fully Stocked"
            subtitle="Industrial-grade snack, drink, and combo machines — installed at no cost, restocked on a regular schedule."
          />
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              {
                img: corporateLobbyImg.url,
                alt: "Free vending machines in a modern corporate business lobby — snack, drink & combo bays",
                title: "Corporate Lobby Placement",
              },
              {
                img: urbanFuelImg.url,
                alt: "Smart touchscreen vending machine with cashless payments in an urban tech office",
                title: "Smart Touchscreen Vending",
              },
              {
                img: smartFridgeImg.url,
                alt: "AI-powered smart fridge and freezer for 24/7 office breakrooms — fresh food and drinks",
                title: "Smart Fridge & 24H Micro-Market",
              },
            ].map((p) => (
              <div
                key={p.title}
                className="rounded-xl border border-border bg-card overflow-hidden hover:border-safety transition group"
              >
                <div className="aspect-[4/3] bg-secondary flex items-center justify-center p-4">
                  <img
                    src={p.img}
                    alt={p.alt}
                    width={400}
                    height={300}
                    className="max-h-full max-w-full object-contain group-hover:scale-105 transition"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="p-5 border-t border-border">
                  <div className="font-display text-lg uppercase">{p.title}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    100% Free placement · Restocked weekly
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nationwide Coverage — USA Map */}
      <section id="coverage" className="py-20 md:py-24 bg-secondary">
        <div className="container-x">
          <div className="text-center max-w-3xl mx-auto">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-safety">
              USA Coverage
            </div>
            <h2 className="mt-2 font-display text-3xl md:text-5xl uppercase">
              Free Vending Machines — USA Nationwide Coverage
            </h2>
            <p className="mt-4 text-base md:text-lg text-muted-foreground">
              We provide FREE vending machine placement, installation, restocking, and maintenance
              across all 50 states.
            </p>
          </div>
          <div className="mt-12 rounded-xl bg-card border border-border p-4 md:p-8 shadow-[var(--shadow-industrial)]">
            <Suspense
              fallback={
                <div
                  aria-label="USA coverage map loading"
                  className="w-full aspect-[16/9] rounded-lg bg-secondary animate-pulse"
                />
              }
            >
              <USAMap />
            </Suspense>
          </div>
          <div className="mt-10 text-center">
            <a
              href="#contact"
              className="inline-flex items-center gap-2 rounded-md bg-safety px-7 py-3.5 font-semibold text-safety-foreground shadow-[var(--shadow-cta)] hover:translate-y-[-1px] transition"
            >
              Get FREE Vending Machines — USA Nationwide Services <ArrowRight className="h-4 w-4" />
            </a>
            <div className="mt-4 text-sm">
              <Link
                to="/coverage"
                className="text-steel hover:text-safety font-semibold underline underline-offset-4"
              >
                Explore the full USA coverage map →
              </Link>
            </div>
          </div>

          <div className="mt-14">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-safety text-center mb-4">
              All 50 States
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-6 gap-y-2.5">
              {STATES.map((s) => (
                <div key={s} className="flex items-center gap-2 text-sm">
                  <MapPin className="h-3.5 w-3.5 text-safety flex-shrink-0" />
                  <span>{s}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-24 bg-charcoal text-charcoal-foreground">
        <div className="container-x">
          <SectionHeader
            light
            eyebrow="Why Us"
            title="Why Choose Vending Machine Free.com"
            subtitle="Industrial-grade machines, nationwide service, zero out-of-pocket cost."
          />
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {REASONS.map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="rounded-lg border border-white/10 bg-white/[0.03] p-6 hover:border-safety transition"
              >
                <div className="h-12 w-12 rounded-md bg-safety text-safety-foreground flex items-center justify-center">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 font-display text-xl uppercase">{title}</h3>
                <p className="mt-2 text-sm text-white/70 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 md:py-24">
        <div className="container-x max-w-4xl">
          <SectionHeader
            eyebrow="FAQ"
            title="Frequently Asked Questions"
            subtitle="Everything you need to know about free vending placement."
          />
          <div className="mt-10 divide-y divide-border border border-border rounded-xl bg-card">
            {FAQS.map(({ q, a }) => (
              <details key={q} className="group p-6">
                <summary className="flex cursor-pointer items-center justify-between gap-4 font-semibold text-base list-none">
                  <span>{q}</span>
                  <span className="h-7 w-7 rounded-md bg-secondary flex items-center justify-center text-safety group-open:rotate-45 transition">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" ref={contactRef} className="py-20 md:py-24 bg-secondary">
        <div className="container-x grid md:grid-cols-2 gap-12 items-start">
          <div>
            <SectionHeader
              eyebrow="Get Started"
              title="Apply for Your Free Vending Machine"
              subtitle="Tell us about your location. We'll respond within 24 hours with next steps."
              align="left"
            />
            <ul className="mt-8 space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-safety mt-0.5" />
                Free site survey & machine recommendation
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-safety mt-0.5" />
                Delivery, install, and stocking included
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-safety mt-0.5" />
                Ongoing maintenance & restocking at no cost
              </li>
            </ul>
            <div className="mt-10 rounded-lg border border-border bg-card p-5">
              <h3 className="font-display text-base uppercase tracking-wide">How to reach us</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                To keep response times fast and inquiries organized, we don't publish a public phone
                number or email inbox. Submit the application form on this page and a real human
                from our placement team will reply within 24 hours with next steps, a site survey
                time, and machine recommendations.
              </p>
              <div className="mt-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                  Follow us
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: "Facebook", href: "https://www.facebook.com/vendingmachinesfree" },
                    {
                      label: "LinkedIn",
                      href: "https://www.linkedin.com/company/vendingmachines-free",
                    },
                    { label: "Instagram", href: "https://www.instagram.com/vendingmachinesfree" },
                    { label: "X / Twitter", href: "https://twitter.com/vending_free" },
                  ].map((s) => (
                    <a
                      key={s.label}
                      href={s.href}
                      rel="noopener noreferrer me"
                      target="_blank"
                      className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium hover:border-safety hover:text-safety transition"
                    >
                      {s.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <form
            noValidate
            name="Apply for Your Free Vending Machine"
            aria-label="Apply for Your Free Vending Machine"
            title="Apply for Your Free Vending Machine"
            onSubmit={async (e) => {
              e.preventDefault();
              setFormError(null);
              setFieldErrors({});

              const formEl = e.currentTarget;
              const fd = new FormData(formEl);
              const payload: Record<string, unknown> = {};
              fd.forEach((value, key) => {
                if (key === "machine_type") return; // handled as multi-select below
                payload[key] = typeof value === "string" ? value : "";
              });
              // Collect all selected machine types (checkboxes → array)
              const machineTypes = fd
                .getAll("machine_type")
                .filter((v): v is string => typeof v === "string" && v.length > 0);
              payload.machine_types = machineTypes;
              if (TURNSTILE_ENV_KEY) payload.turnstile_token = captchaToken;
              // Attach UTM parameters (captured on page load)
              for (const [k, v] of Object.entries(utm)) {
                if (v) payload[k] = v;
              }

              // Client-side required-field check (fast UX, before network)
              const required = [
                "full_name",
                "business_name",
                "street",
                "city",
                "zip",
                "email",
                "phone",
                "state",
              ];
              const clientErrors: FieldErrors = {};
              for (const k of required) {
                if (!String(payload[k] ?? "").trim()) clientErrors[k] = "This field is required";
              }
              const emailVal = String(payload.email ?? "");
              if (emailVal && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal))
                clientErrors.email = "Enter a valid email address";
              if (TURNSTILE_ENV_KEY && !captchaToken)
                clientErrors.turnstile_token = "Please complete the CAPTCHA";

              if (Object.keys(clientErrors).length > 0) {
                setFieldErrors(clientErrors);
                setFormError("Please fix the highlighted fields and try again.");
                try {
                  const w = window as unknown as {
                    gtag?: (...args: unknown[]) => void;
                    dataLayer?: unknown[];
                  };
                  const evt = {
                    form_id: "homepage_apply",
                    form_name: "Apply for Your Free Vending Machine",
                    form_location: "homepage",
                    error_type: "client_validation",
                    invalid_fields: Object.keys(clientErrors).join(","),
                    field_count: Object.keys(clientErrors).length,
                    ...utm,
                  };
                  w.gtag?.("event", "form_validation_error", evt);
                  (w.dataLayer = w.dataLayer || []).push({
                    event: "form_validation_error",
                    ...evt,
                  });
                } catch {
                  /* analytics best-effort */
                }
                return;
              }

              setSubmitting(true);
              try {
                // GA4: form submit attempt
                try {
                  const w = window as unknown as {
                    gtag?: (...args: unknown[]) => void;
                    dataLayer?: unknown[];
                  };
                  const evt = {
                    form_id: "homepage_apply",
                    form_name: "Apply for Your Free Vending Machine",
                    form_location: "homepage",
                    ...utm,
                  };
                  w.gtag?.("event", "form_submit_attempt", evt);
                  (w.dataLayer = w.dataLayer || []).push({ event: "form_submit_attempt", ...evt });
                } catch {
                  /* analytics best-effort */
                }
                const res = await fetch("/api/public/apply", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify(payload),
                });
                if (!res.ok) {
                  const body = (await res.json().catch(() => ({}))) as {
                    error?: string;
                    issues?: { fieldErrors?: Record<string, string[]> };
                  };
                  // Rate limited — surface a distinct, friendly message
                  if (res.status === 429) {
                    const retryAfter = Number(res.headers.get("Retry-After")) || 60;
                    const msg = `Too many submissions from your network. Please wait ${retryAfter}s and try again.`;
                    setFormError(msg);
                    try {
                      const w = window as unknown as {
                        gtag?: (...args: unknown[]) => void;
                        dataLayer?: unknown[];
                      };
                      const evt = {
                        form_id: "homepage_apply",
                        form_location: "homepage",
                        reason: "rate_limited",
                        retry_after: retryAfter,
                        ...utm,
                      };
                      w.gtag?.("event", "form_submit_error", evt);
                      (w.dataLayer = w.dataLayer || []).push({
                        event: "form_submit_error",
                        ...evt,
                      });
                    } catch {
                      /* noop */
                    }
                    throw new Error(msg);
                  }
                  const serverFieldErrors: FieldErrors = {};
                  const fe = body?.issues?.fieldErrors ?? {};
                  for (const [k, v] of Object.entries(fe)) {
                    if (Array.isArray(v) && v[0]) serverFieldErrors[k] = v[0];
                  }
                  setFieldErrors(serverFieldErrors);
                  const firstField = Object.keys(serverFieldErrors)[0];
                  const firstMsg = firstField ? serverFieldErrors[firstField] : undefined;
                  // Scroll the first failing field into view
                  if (firstField && typeof document !== "undefined") {
                    const el = document.querySelector<HTMLElement>(`[name="${firstField}"]`);
                    el?.scrollIntoView({ behavior: "smooth", block: "center" });
                    el?.focus?.();
                  }
                  // Turnstile-specific: reset widget so user can retry
                  if (serverFieldErrors.turnstile_token) {
                    try {
                      const w = window as unknown as {
                        turnstile?: { reset: (sel?: string) => void };
                      };
                      w.turnstile?.reset(".cf-turnstile");
                    } catch {
                      /* noop */
                    }
                    setCaptchaToken("");
                  }
                  const readable =
                    body?.error === "Validation failed" && firstMsg
                      ? `${firstMsg}${firstField ? ` (${firstField.replace(/_/g, " ")})` : ""}`
                      : (body?.error ?? "Something went wrong submitting your application.");
                  throw new Error(readable);
                }
                // GA4 conversion event
                try {
                  const w = window as unknown as {
                    gtag?: (...args: unknown[]) => void;
                    dataLayer?: unknown[];
                  };
                  const payloadEvt = {
                    form_id: "homepage_apply",
                    form_name: "Get FREE Vending Machines",
                    form_location: "homepage",
                    state: payload.state || undefined,
                    machine_types:
                      Array.isArray(payload.machine_types) && payload.machine_types.length
                        ? (payload.machine_types as string[]).join(",")
                        : undefined,
                    ...utm,
                  };

                  if (typeof w.gtag === "function") {
                    w.gtag("event", "generate_lead", { ...payloadEvt, value: 1, currency: "USD" });
                    w.gtag("event", "form_submit", payloadEvt);
                  } else {
                    (w.dataLayer = w.dataLayer || []).push({
                      event: "generate_lead",
                      ...payloadEvt,
                    });
                  }
                } catch (e) {
                  console.warn("[ga4] event failed", e);
                }
                navigate({ to: "/thank-you" });
              } catch (err) {
                const msg =
                  err instanceof Error
                    ? err.message
                    : "Could not submit your application. Please try again.";
                setFormError(msg);
                setSubmitting(false);
                try {
                  const w = window as unknown as {
                    gtag?: (...args: unknown[]) => void;
                    dataLayer?: unknown[];
                  };
                  const evt = {
                    form_id: "homepage_apply",
                    form_location: "homepage",
                    reason: msg.slice(0, 120),
                    ...utm,
                  };
                  w.gtag?.("event", "form_submit_error", evt);
                  (w.dataLayer = w.dataLayer || []).push({ event: "form_submit_error", ...evt });
                } catch {
                  /* analytics best-effort */
                }
              }
            }}
            className="rounded-xl bg-card p-6 md:p-8 border border-border shadow-[var(--shadow-industrial)]"
          >
            <fieldset disabled={submitting} className="space-y-8">
              {/* Honeypot — must remain empty; hidden from real users */}
              <div
                aria-hidden="true"
                className="absolute left-[-10000px] top-auto h-px w-px overflow-hidden"
              >
                <label>
                  Website
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    defaultValue=""
                  />
                </label>
              </div>
              <input type="hidden" name="form_started_at" value={formStartedAt} readOnly />

              {/* CONTACT DETAILS */}
              <div>
                <legend className="text-xs font-bold uppercase tracking-[0.2em] text-safety mb-4">
                  Contact Details
                </legend>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field
                    label="Full Name"
                    name="full_name"
                    required
                    autoComplete="name"
                    error={fieldErrors.full_name}
                  />
                  <Field
                    label="Business Name"
                    name="business_name"
                    required
                    autoComplete="organization"
                    error={fieldErrors.business_name}
                  />
                  <div className="sm:col-span-2">
                    <Field
                      label="Street Address"
                      name="street"
                      required
                      autoComplete="street-address"
                      error={fieldErrors.street}
                    />
                  </div>
                  <Field
                    label="City"
                    name="city"
                    required
                    autoComplete="address-level2"
                    error={fieldErrors.city}
                  />
                  <Field
                    label="ZIP Code"
                    name="zip"
                    required
                    autoComplete="postal-code"
                    inputMode="numeric"
                    error={fieldErrors.zip}
                  />
                  <Field
                    label="Email Address"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    error={fieldErrors.email}
                  />
                  <Field
                    label="Phone Number"
                    name="phone"
                    type="tel"
                    required
                    autoComplete="tel"
                    error={fieldErrors.phone}
                  />
                  <div className="sm:col-span-2">
                    <label
                      htmlFor="field-state"
                      className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5"
                    >
                      State<span className="text-destructive ml-1">*</span>
                    </label>
                    <select
                      id="field-state"
                      name="state"
                      required
                      defaultValue=""
                      aria-invalid={fieldErrors.state ? true : undefined}
                      className={`w-full rounded-md border bg-background px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring ${
                        fieldErrors.state ? "border-destructive" : "border-input"
                      }`}
                    >
                      <option value="" disabled>
                        Select your state…
                      </option>
                      {US_STATES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                    {fieldErrors.state && (
                      <p className="mt-1 text-xs text-destructive">{fieldErrors.state}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* BUSINESS QUALIFICATION */}
              <div>
                <legend className="text-xs font-bold uppercase tracking-[0.2em] text-safety mb-4">
                  Business Qualification
                </legend>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field
                    label="Number of Employees"
                    name="employees"
                    type="number"
                    inputMode="numeric"
                    placeholder="e.g. 25"
                  />
                  <Field
                    label="Daily Visitors"
                    name="daily_visitors"
                    type="number"
                    inputMode="numeric"
                    placeholder="e.g. 150"
                  />
                  <div className="sm:col-span-2">
                    <label
                      htmlFor="field-business-hours"
                      className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5"
                    >
                      Business Hours
                    </label>
                    <select
                      id="field-business-hours"
                      name="business_hours"
                      defaultValue=""
                      className="w-full rounded-md border border-input bg-background px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="" disabled>
                        Select operating days…
                      </option>
                      {HOURS_OPTIONS.map((h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-5">
                  <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                    Operator Parking / Refill Access
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {ACCESS_OPTIONS.map((opt) => (
                      <label
                        key={opt}
                        className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm cursor-pointer hover:border-safety has-[:checked]:border-safety has-[:checked]:bg-safety/10 has-[:checked]:text-foreground"
                      >
                        <input
                          type="radio"
                          name="access_level"
                          value={opt}
                          className="accent-safety"
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="mt-5">
                  <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                    Machine Types Required{" "}
                    <span className="normal-case font-normal text-muted-foreground">
                      (select all that apply)
                    </span>
                  </span>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {MACHINE_OPTIONS.map((opt) => (
                      <label
                        key={opt}
                        className="inline-flex items-center justify-center gap-2 rounded-md border border-input bg-background px-3 py-2.5 text-sm cursor-pointer hover:border-safety has-[:checked]:border-safety has-[:checked]:bg-safety/10 has-[:checked]:text-foreground"
                      >
                        <input
                          type="checkbox"
                          name="machine_type"
                          value={opt}
                          className="accent-safety"
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="mt-5">
                  <label
                    htmlFor="field-location-notes"
                    className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5"
                  >
                    Tell Us About Your Location{" "}
                    <span className="normal-case font-normal text-muted-foreground">
                      (optional)
                    </span>
                  </label>
                  <textarea
                    id="field-location-notes"
                    name="location_notes"
                    rows={4}
                    placeholder="Break room layout, foot traffic patterns, preferred products, etc."
                    className="w-full rounded-md border border-input bg-background px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </div>

              {/* OFFICE PROFILE — lead qualification for tech / corporate placements */}
              <div>
                <legend className="text-xs font-bold uppercase tracking-[0.2em] text-safety mb-2">
                  Office Profile{" "}
                  <span className="normal-case font-normal text-muted-foreground tracking-normal">
                    (optional — helps us match the right operator faster)
                  </span>
                </legend>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="field-office-type" className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                      Office Type
                    </label>
                    <select
                      id="field-office-type"
                      name="office_type"
                      defaultValue=""
                      className="w-full rounded-md border border-input bg-background px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="">Select…</option>
                      <option>Private / enclosed office</option>
                      <option>Shared building lobby</option>
                      <option>Open-plan tech campus</option>
                      <option>Warehouse / manufacturing floor</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="field-building-footprint" className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                      Building Footprint
                    </label>
                    <select
                      id="field-building-footprint"
                      name="building_footprint"
                      defaultValue=""
                      className="w-full rounded-md border border-input bg-background px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="">Select…</option>
                      <option>Single-tenant</option>
                      <option>Shared multi-tenant</option>
                    </select>
                  </div>
                  <Field
                    label="Total Building Headcount (if shared)"
                    name="total_building_headcount"
                    type="number"
                    inputMode="numeric"
                    placeholder="e.g. 250"
                  />
                  <div>
                    <label htmlFor="field-shift-pattern" className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                      Shift Pattern
                    </label>
                    <select
                      id="field-shift-pattern"
                      name="shift_pattern"
                      defaultValue=""
                      className="w-full rounded-md border border-input bg-background px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="">Select…</option>
                      <option>In-office 5 days</option>
                      <option>Hybrid 3–4 days</option>
                      <option>24/7 operation</option>
                      <option>Flexible / varies</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="field-service-interest" className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                      Service You're Interested In
                    </label>
                    <select
                      id="field-service-interest"
                      name="service_interest"
                      defaultValue=""
                      className="w-full rounded-md border border-input bg-background px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="">Select…</option>
                      <option>Traditional vending</option>
                      <option>Micro-market</option>
                      <option>Smart fridge</option>
                      <option>Not sure yet</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* CAPTCHA */}
              {TURNSTILE_ENV_KEY ? (
                <div>
                  <div
                    className="cf-turnstile"
                    data-sitekey={turnstileSiteKey}
                    data-callback="__vmTurnstileCb"
                    data-expired-callback="__vmTurnstileExpired"
                    data-error-callback="__vmTurnstileError"
                    data-timeout-callback="__vmTurnstileExpired"
                    data-theme="light"
                  />
                  {captchaStatus === "loading" && (
                    <p className="mt-1 text-xs text-muted-foreground">Loading CAPTCHA…</p>
                  )}
                  {captchaStatus === "expired" && (
                    <p className="mt-1 text-xs text-destructive">
                      CAPTCHA expired.{" "}
                      <button type="button" onClick={resetCaptcha} className="underline">
                        Retry
                      </button>
                    </p>
                  )}
                  {captchaStatus === "error" && (
                    <p className="mt-1 text-xs text-destructive">
                      CAPTCHA failed to load.{" "}
                      <button type="button" onClick={resetCaptcha} className="underline">
                        Try again
                      </button>
                    </p>
                  )}
                  {fieldErrors.turnstile_token && captchaStatus === "ready" && (
                    <p className="mt-1 text-xs text-destructive">{fieldErrors.turnstile_token}</p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Protected by spam filtering. Add a Cloudflare Turnstile site key (
                  <code>VITE_TURNSTILE_SITE_KEY</code>) to enable the CAPTCHA widget.
                </p>
              )}

              {formError && (
                <p className="text-sm text-destructive" role="alert">
                  {formError}
                </p>
              )}

              <button
                type="submit"
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-safety px-5 py-3.5 font-semibold text-safety-foreground shadow-[var(--shadow-cta)] hover:translate-y-[-1px] transition disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {submitting ? "Submitting…" : "Get FREE Vending Machines — USA Nationwide Services"}
                {!submitting && <ArrowRight className="h-4 w-4" />}
              </button>
              <p className="text-xs text-muted-foreground text-center">
                By submitting you agree to be contacted about your free vending placement.
              </p>
            </fieldset>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-charcoal text-charcoal-foreground py-12">
        <div className="container-x flex flex-col items-center gap-6">
          <p className="text-center text-base md:text-lg font-semibold text-safety uppercase tracking-wide">
            Powered by American businesses — get a Free Vending Machine Nationwide.
          </p>
          <div className="w-full flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2 bg-white/95 rounded-md px-3 py-2">
              <img
                src={logo400.url}
                srcSet={`${logo400.url} 400w, ${logo800.url} 800w`}
                sizes="240px"
                alt="Vending Machine Free — Nationwide USA vending machine placement"
                width={1920}
                height={960}
                loading="lazy"
                decoding="async"
                className="h-12 md:h-14 w-auto"
              />
            </div>
            <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-white/80">
              <a href="/about" className="hover:text-safety">
                About Us
              </a>
              <a href="/free-vending-machines-usa" className="hover:text-safety">
                Free Vending USA
              </a>
              <a href="/free-vending-machine-placement" className="hover:text-safety">
                Free Placement
              </a>
              <a href="/vending-machine-placement" className="hover:text-safety">
                Placement
              </a>
              <a href="/vending-machines" className="hover:text-safety">
                Machines
              </a>
              <a href="/coverage" className="hover:text-safety">
                Coverage
              </a>
              <a href="/contact" className="hover:text-safety">
                Apply
              </a>
              <a href="/sitemap" className="hover:text-safety">
                Site Map
              </a>
              <a href="/sitemap.xml" className="hover:text-safety">
                Sitemap.xml
              </a>
              <a href="/terms" className="hover:text-safety">
                Terms
              </a>
            </nav>
            <p className="text-sm text-white/60">
              © {new Date().getFullYear()} Vending Machine Free.com · Nationwide USA Vending
              Placement
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function SectionHeader({
  eyebrow,
  title,
  subtitle,
  align = "center",
  light = false,
}: {
  eyebrow: string;
  title: string;
  subtitle?: string;
  align?: "center" | "left";
  light?: boolean;
}) {
  const center = align === "center";
  return (
    <div className={center ? "text-center max-w-2xl mx-auto" : "max-w-2xl"}>
      <div
        className={`text-xs font-bold uppercase tracking-[0.2em] ${light ? "text-safety" : "text-safety"}`}
      >
        {eyebrow}
      </div>
      <h2 className="mt-2 font-display text-3xl md:text-4xl uppercase">{title}</h2>
      {subtitle && (
        <p className={`mt-3 text-base ${light ? "text-white/70" : "text-muted-foreground"}`}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

function Field({
  label,
  error,
  ...rest
}: { label: string; error?: string } & InputHTMLAttributes<HTMLInputElement>) {
  const fieldId = rest.id ?? (rest.name ? `field-${rest.name}` : undefined);
  return (
    <div>
      <label
        htmlFor={fieldId}
        className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5"
      >
        {label}
        {rest.required && <span className="text-destructive ml-1">*</span>}
      </label>
      <input
        {...rest}
        id={fieldId}
        type={rest.type ?? "text"}
        aria-invalid={error ? true : undefined}
        aria-describedby={error && rest.name ? `${rest.name}-error` : undefined}
        className={`w-full rounded-md border bg-background px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring ${
          error ? "border-destructive" : "border-input"
        }`}
      />
      {error && (
        <p
          id={rest.name ? `${rest.name}-error` : undefined}
          className="mt-1 text-xs text-destructive"
        >
          {error}
        </p>
      )}
    </div>
  );
}
